import { AlertTriangle, RefreshCw, Volume2 } from "lucide-react";
import { useMemo, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { DashboardRequestError } from "../providers/aws/client";
import { AccentFidelityUsagePanel } from "./AccentFidelityUsagePanel";
import { AddVoiceDialog } from "./AddVoiceDialog";
import {
  filterSoundVoices,
  initialSoundFilters,
  sortSoundVoices,
  type SoundFilters,
} from "./catalogState";
import { useSoundVoices } from "./api";
import { SoundFiltersBar } from "./SoundFiltersBar";
import { ProductionSoundDrafts } from "./ProductionSoundDrafts";
import { SoundRenderProfilesPanel } from "./SoundRenderProfilesPanel";
import { SoundAuditionsPanel } from "./SoundAuditionsPanel";
import { NarrationLocalesPanel } from "./NarrationLocalesPanel";
import { VoiceCatalogTable } from "./VoiceCatalogTable";
import { VoicePreviewProvider } from "./VoicePreviewProvider";

export function SoundPage() {
  const query = useSoundVoices();
  const [filters, setFilters] = useState<SoundFilters>(initialSoundFilters);
  const payload = query.data;
  const voices = useMemo(
    () => sortSoundVoices(filterSoundVoices(payload?.voices ?? [], filters)),
    [filters, payload?.voices],
  );
  const pendingVoiceDraftIds = useMemo(
    () =>
      new Map(
        (payload?.pending_drafts ?? []).flatMap((draft) =>
          [
            ...draft.voice_diff.added_voice_ids,
            ...draft.voice_diff.removed_voice_ids,
            ...(draft.voice_diff.disabled_voice_ids ?? []),
            ...draft.voice_diff.changed_voice_ids,
          ].map((voiceId) => [voiceId, draft.id] as const),
        ),
      ),
    [payload?.pending_drafts],
  );

  if (query.isLoading) {
    return (
      <main className="p-6">
        <p className="text-sm text-muted-foreground">Loading voices…</p>
      </main>
    );
  }

  if (query.error || !payload) {
    const error = query.error;
    const isUnauthorized =
      error instanceof DashboardRequestError &&
      (error.status === 401 || error.status === 403);
    return (
      <main className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Sound</h1>
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>
            {isUnauthorized
              ? "Administrator access required"
              : "The voice list could not be loaded"}
          </AlertTitle>
          <AlertDescription>
            {isUnauthorized
              ? "Ask an administrator to give your account access to the Sound page."
              : error instanceof Error
                ? error.message
                : "Something went wrong while loading the voices. Try Refresh in a moment."}
          </AlertDescription>
        </Alert>
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          <RefreshCw
            className={`mr-2 size-4 ${query.isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </main>
    );
  }

  const isProduction = payload.environment === "stg";

  return (
    <VoicePreviewProvider>
      <main className="space-y-4 p-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Volume2 className="size-6" />
              <h1 className="text-2xl font-semibold">Sound</h1>
              <Badge variant={isProduction ? "destructive" : "secondary"}>
                {isProduction ? "Production" : "Staging"}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              The narrator voices used in Echoes. Press play to hear a sample of
              any voice — previews are free.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {payload.summary.active_b2c} voice
              {payload.summary.active_b2c === 1 ? "" : "s"} live for customers ·{" "}
              {payload.summary.provider_total} in the ElevenLabs library ·
              updated {new Date(payload.generated_at).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={query.isFetching}
              onClick={() => query.refetch()}
            >
              <RefreshCw
                className={`mr-2 size-4 ${query.isFetching ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <AddVoiceDialog payload={payload} />
          </div>
        </header>
        <NarrationLocalesPanel value={payload.narration_locales} />

        {payload.provider_status !== "ok" ? (
          <Alert
            variant={
              payload.provider_status === "unavailable"
                ? "destructive"
                : "default"
            }
          >
            <AlertTriangle />
            <AlertTitle>
              {payload.provider_status === "unavailable"
                ? "ElevenLabs can't be reached right now"
                : "The voice list may be slightly out of date"}
            </AlertTitle>
            <AlertDescription>
              Showing the last saved list. Nothing is lost — try Refresh in a
              few minutes.
            </AlertDescription>
          </Alert>
        ) : null}

        {isProduction ? (
          <ProductionSoundDrafts
            activeReleaseId={payload.active_release?.id ?? null}
            drafts={payload.pending_drafts}
          />
        ) : null}

        <SoundAuditionsPanel
          key={payload.auditions.latest_manifest?.manifest_digest ?? "no-pack"}
          auditions={payload.auditions}
          voices={payload.voices}
          refreshing={query.isFetching}
          onRefresh={() => void query.refetch()}
        />

        <SoundFiltersBar
          voices={payload.voices}
          filters={filters}
          onChange={setFilters}
        />
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <p>
            Showing {voices.length} of {payload.voices.length} voices.
          </p>
          <p>Use the pinned Actions column to turn catalog voices on or off.</p>
        </div>
        <VoiceCatalogTable
          environment={payload.environment}
          payload={payload}
          pendingVoiceDraftIds={pendingVoiceDraftIds}
          voices={voices}
          onClearFilters={() => setFilters(initialSoundFilters)}
        />

        <AccentFidelityUsagePanel usage={payload.summary.accent_fidelity} />

        <Accordion type="single" collapsible>
          <AccordionItem value="render-profiles" className="border-none">
            <AccordionTrigger className="text-sm text-muted-foreground">
              Technical details
            </AccordionTrigger>
            <AccordionContent>
              <SoundRenderProfilesPanel profiles={payload.render_profiles} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </main>
    </VoicePreviewProvider>
  );
}

SoundPage.path = "/sound";
